import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Body2,
  Caption1,
  Card,
  CompleteCheckbox,
  Header3,
  Link,
  RocketSmallIcon,
  SkeletonLoader,
} from "@activecollab/components";
import classNames from "classnames";
import { CallToUpgradeLink } from "~/components/CallToUpgradeLink/CallToUpgradeLink";
import ErrorBoundary from "~/components/ErrorBoundry";
import { IntegrationCard } from "~/components/Integrations/IntegrationCard";
import { Page } from "~/components/Page";
import { useIntegrations } from "~/repository";
import { getIsOnDemand, getRawConfigOption } from "~/store/selectors/selector.configOptions";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";
import Angie from "~/utils/angie";
import { assets_url } from "~/utils/urls";
import "./AppsAndIntegrationsPage.css";
import ApiTokensCard from "./Intergations/ApiTokens/ApiTokensCard";

const AppsAndIntegrationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isFetched } = useIntegrations();
  const isOnDemand = useSelector(getIsOnDemand);
  const isMcpServerFeatureFlagOn = useSelector((state) => getFeatureFlag(state, "mcp_server"));
  const isMcpServerFeatureOn = useSelector((state) => getRawConfigOption(state, "mcp_integration_enabled"));
  const payPalExpressIntegration = useMemo(() => data?.find((i) => i.short_name === "paypal-express"), [data]);
  const stripeIntegration = useMemo(() => data?.find((i) => i.short_name === "stripe"), [data]);

  // temporarily check if QuickBooks is in use to hide Payment Gateways section, until it's integration is finished
  const quickBooksIntegrationData = useMemo(() => data?.find((i) => i.short_name === "quickbooks"), [data]);

  const otherIntegrations = useMemo(() => {
    let result = {
      slack: {
        context: "Slack Integration",
        img: `${Angie.assets_url}/system/images/integrations/slack.png`,
        title: "Slack",
        description: t("Get ActiveCollab updates in Slack") + ".",
        features: ["slack_integration"],
        hasFeature: false,
        finished: true,
      },
      quickbooks: {
        context: "QuickBooks Integration",
        img: `${Angie.assets_url}/system/images/integrations/quickbooks.png`,
        title: "QuickBooks",
        description: t("Create QuickBooks invoices from billable time and expenses") + ".",
        features: ["quickbooks_integration"],
        hasFeature: false,
        finished: true,
      },
      zapier: {
        context: "Zapier Integration",
        img: `${Angie.assets_url}/system/images/integrations/zapier.png`,
        title: "Zapier",
        description: t("Connect ActiveCollab to other apps") + ".",
        features: ["zapier_integration"],
        hasFeature: false,
        finished: true,
      },
      webhooks: {
        context: "Webhooks Integration",
        img: `${Angie.assets_url}/system/images/integrations/webhooks.png`,
        title: "Webhooks",
        description: t("Notify 3rd party services about what's happening in ActiveCollab") + ".",
        features: ["webhooks_integration"],
        hasFeature: false,
        finished: true,
      },
      "google-drive": {
        context: "Google Drive Integration",
        img: `${Angie.assets_url}/system/images/integrations/google_drive.png`,
        title: "Google Drive",
        description: t("Connect your Google Drive files to ActiveCollab") + ".",
        features: ["files"],
        hasFeature: true,
        finished: true,
      },
      dropbox: {
        context: "Dropbox Integrations",
        img: `${Angie.assets_url}/system/images/integrations/dropbox.png`,
        title: "Dropbox",
        description: t("Connect your Dropbox files to ActiveCollab") + ".",
        features: ["files"],
        hasFeature: true,
        finished: true,
      },
      mcp: {
        context: "Model Context Protocol Integration",
        img: `${Angie.assets_url}/system/images/integrations/mcp-server.png`,
        title: "MCP",
        description: t("Plug AI models into external tools and data sources") + ".",
        features: ["mcp_server_integration"],
        hasFeature: true,
        finished: true,
        short_name: "mcp",
        url: "/integrations/mcp",
      },
    };
    if (data) {
      data.reduce((acc, next) => {
        if (result[next.short_name]) {
          let url = next.already_connected[0]?.url_path;
          const isFinished = result[next.short_name].finished;
          if (!isFinished) {
            url = `${Angie.activecollab_url}${url}`;
          }
          result[next.short_name] = {
            ...result[next.short_name],
            url: url,
            hasFeature: true,
            integration: next,
          };
        }
      }, result);
    }
    return result;
  }, [data, t]);

  const slackIntegration = otherIntegrations.slack;
  const quickbooksIntegration = otherIntegrations.quickbooks;
  const zapierIntegration = otherIntegrations.zapier;
  const webhooksIntegration = otherIntegrations.webhooks;
  const googleDriveIntegration = otherIntegrations["google-drive"];
  const dropBoxIntegration = otherIntegrations.dropbox;
  const mcpServerIntegration = otherIntegrations.mcp;
  mcpServerIntegration.hasFeature = isMcpServerFeatureFlagOn && !!isMcpServerFeatureOn;

  const logIntegrationClick = useCallback(
    (integration) => {
      if (integration) {
        Angie.logger
          .info(
            `User opened ${integration.name} integration settings`,
            {
              integration_name: integration.name,
              already_in_use: integration.is_in_use,
            },
            true
          )
          .then(() => {
            navigate(integration?.short_name === `paypal-express` ? "paypal" : "stripe");
          });
      }
    },
    [navigate]
  );

  const loader = useMemo(() => {
    return (
      <>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Card
            variant="paper-2"
            className="tw-min-h-[120px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-6 tw-py-6 tw-gap-24"
          >
            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
              <SkeletonLoader className="tw-w-full tw-h-5" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
            </div>
            <div>
              <SkeletonLoader className="tw-h-14 tw-w-14 tw-rounded-full" />
            </div>
          </Card>
          <Card
            variant="paper-2"
            className="tw-min-h-[120px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-6 tw-py-6 tw-gap-24"
          >
            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
              <SkeletonLoader className="tw-w-full tw-h-5" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
            </div>
            <div>
              <SkeletonLoader className="tw-h-14 tw-w-14 tw-rounded-full" />
            </div>
          </Card>
        </div>
        <div className="tw-mt-6">
          <SkeletonLoader className="tw-w-full tw-h-4" />
        </div>
        <div className="tw-mt-3">
          <Card
            variant="paper-2"
            className="tw-min-h-[70px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-6 tw-py-6 tw-gap-4"
          >
            <div>
              <SkeletonLoader className="tw-h-8 tw-w-8 tw-rounded-full" />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
              <SkeletonLoader className="tw-w-full tw-h-4" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
            </div>
          </Card>
        </div>
        <div className="tw-mt-6">
          <SkeletonLoader className="tw-w-full tw-h-4" />
        </div>
        <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1">
          <Card
            variant="paper-2"
            className="tw-min-h-[70px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-6 tw-py-6 tw-gap-4"
          >
            <div>
              <SkeletonLoader className="tw-h-8 tw-w-8 tw-rounded-full" />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
              <SkeletonLoader className="tw-w-full tw-h-4" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
            </div>
          </Card>
          <Card
            variant="paper-2"
            className="tw-min-h-[70px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-6 tw-py-6 tw-gap-4"
          >
            <div>
              <SkeletonLoader className="tw-h-8 tw-w-8 tw-rounded-full" />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
              <SkeletonLoader className="tw-w-full tw-h-4" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
            </div>
          </Card>
          <Card
            variant="paper-2"
            className="tw-min-h-[70px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-6 tw-py-6 tw-gap-4"
          >
            <div>
              <SkeletonLoader className="tw-h-8 tw-w-8 tw-rounded-full" />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
              <SkeletonLoader className="tw-w-full tw-h-4" />
              <SkeletonLoader className="tw-w-full tw-h-4" />
            </div>
          </Card>
        </div>
      </>
    );
  }, []);

  return (
    <ErrorBoundary errorLocation="AppsAndIntegrationsPage">
      <Page
        title="Apps & Integrations"
        className="tw-max-w-[850px] tw-pb-20"
      >
        <Page.Header
          title="Apps & Integrations"
          className="print:tw-hidden"
        />
        {!isFetched ? loader : null}
        {isFetched ? (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Card
              variant="paper-2"
              className="tw-min-h-[120px] tw-flex tw-items-center tw-justify-between tw-pl-5 tw-pr-6 tw-py-6"
            >
              <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-justify-between tw-gap-4 md:tw-gap-24">
                <div>
                  <Header3 className="tw-mb-2">{t("Download Our Mobile Apps")}</Header3>
                  <Body2 color="tertiary">
                    {t(
                      "Your account, tasks, calendar, stopwatch and notifications in one place, anytime you need them."
                    )}
                  </Body2>
                </div>
                <div className="tw-flex tw-gap-1 tw-items-center tw-h-11">
                  <Link
                    href="https://apps.apple.com/us/app/activecollab-work-management/id1509421965"
                    target="_blank"
                    className="tw-flex tw-w-auto"
                    data-testid="iphone-app"
                  >
                    <img
                      src={`${assets_url}/system/images/layout/download_app_from_app_store.png`}
                      alt={t("Download app from app store")}
                      height={36}
                    />
                  </Link>
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.activecollab.mobile"
                    target="_blank"
                    className="tw-flex tw-w-auto"
                    data-testid="android-app"
                  >
                    <img
                      src={`${assets_url}/system/images/layout/download_app_from_google_play.png`}
                      alt={t("Download app from google play")}
                      height={36}
                    />
                  </Link>
                </div>
              </div>
            </Card>
            {!quickBooksIntegrationData?.is_in_use ? (
              <Card
                variant="paper-2"
                className="tw-min-h-[120px] tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-justify-between tw-mb-1 tw-pl-5 tw-pr-8 tw-py-4 tw-gap-4 md:tw-gap-24"
              >
                <div>
                  <div className="tw-flex tw-items-center tw-mb-2">
                    <Header3>{t("Payment Gateways")}</Header3>
                    {!stripeIntegration && !payPalExpressIntegration ? (
                      <CallToUpgradeLink
                        ownerChildren={
                          <Link
                            as={RouterLink}
                            variant="text colored"
                            className="tw-shrink-0 tw-ml-2"
                          >
                            <RocketSmallIcon
                              className="tw-shrink-0"
                              data-test-id="upgrade-icon"
                            />
                            {t("Upgrade")}
                          </Link>
                        }
                        memberChildren={
                          <Link
                            as={RouterLink}
                            variant="text colored"
                            className="tw-shrink-0 tw-ml-2"
                          >
                            <RocketSmallIcon className="tw-shrink-0" />
                            {t("Upgrade")}
                          </Link>
                        }
                        features={["invoicing"]}
                        context="Payment Gateways Integration"
                        page="AppsAndIntegrations"
                      />
                    ) : null}
                  </div>
                  <Body2 color="tertiary">
                    {t(
                      "Speed up payments and improve cash flow by accepting credit card and PayPal payments on your invoices"
                    )}
                    .
                  </Body2>
                </div>
                <div className="tw-flex tw-gap-4">
                  <div
                    className={classNames(
                      "tw-flex tw-flex-col tw-border-solid tw-border-[1px] tw-border-theme-300 tw-relative tw-items-center tw-justify-center tw-gap-2 tw-p-3 tw-h-[88px] tw-min-w-[96px]",
                      { "tw-cursor-pointer": stripeIntegration }
                    )}
                    onClick={() => {
                      logIntegrationClick(stripeIntegration);
                    }}
                  >
                    <img
                      src={`${assets_url}/system/images/integrations/stripe.png`}
                      alt="Stripe logo"
                      className={classNames("tw-w-10", {
                        "tw-opacity-50": !stripeIntegration || (stripeIntegration && !stripeIntegration.is_in_use),
                      })}
                    />
                    <Caption1
                      whitespace="no-wrap"
                      color={
                        !stripeIntegration ? "tertiary" : stripeIntegration?.is_in_use ? "theme-primary" : "primary"
                      }
                    >
                      Stripe
                    </Caption1>
                    {stripeIntegration?.is_in_use ? (
                      <CompleteCheckbox
                        completed
                        className="tw-absolute tw-top-[-12px] tw-right-[-12px]"
                      />
                    ) : null}
                  </div>
                  <div
                    className={classNames(
                      "tw-flex tw-flex-col tw-border-solid tw-border-[1px] tw-border-theme-300 tw-relative tw-items-center tw-justify-center tw-gap-2 tw-p-3 tw-h-[88px] tw-min-w-[96px]",
                      { "tw-cursor-pointer": payPalExpressIntegration }
                    )}
                    onClick={() => {
                      logIntegrationClick(payPalExpressIntegration);
                    }}
                  >
                    <img
                      src={`${assets_url}/system/images/integrations/paypal.png`}
                      alt="Stripe logo"
                      className={classNames("tw-h-10", {
                        "tw-opacity-50":
                          !payPalExpressIntegration ||
                          (payPalExpressIntegration && !payPalExpressIntegration.is_in_use),
                      })}
                    />
                    <Caption1
                      whitespace="no-wrap"
                      color={
                        !payPalExpressIntegration
                          ? "tertiary"
                          : payPalExpressIntegration?.is_in_use
                          ? "theme-primary"
                          : "primary"
                      }
                    >
                      PayPal Express
                    </Caption1>
                    {payPalExpressIntegration?.is_in_use ? (
                      <CompleteCheckbox
                        completed
                        className="tw-absolute tw-top-[-12px] tw-right-[-12px]"
                      />
                    ) : null}
                  </div>
                </div>
              </Card>
            ) : null}
          </div>
        ) : null}
        {isFetched ? (
          <>
            <Body2 className="tw-mt-7 tw-mb-2">{t("Accounting")}</Body2>
            <IntegrationCard
              features={quickbooksIntegration.features}
              context={quickbooksIntegration.context}
              url={quickbooksIntegration.url}
              title={quickbooksIntegration.title}
              hasFeature={quickbooksIntegration.hasFeature}
              img={quickbooksIntegration.img}
              description={quickbooksIntegration.description}
              integration={quickbooksIntegration.integration}
              finished={quickbooksIntegration.finished}
            />
            <>
              <Body2 className="tw-mt-7 tw-mb-2">{t("Notifications")}</Body2>
              <IntegrationCard
                features={slackIntegration.features}
                context={slackIntegration.context}
                url={slackIntegration.url}
                title={slackIntegration.title}
                hasFeature={slackIntegration.hasFeature}
                img={slackIntegration.img}
                description={slackIntegration.description}
                integration={slackIntegration.integration}
                finished={slackIntegration.finished}
              />
            </>

            {!isOnDemand ? (
              <>
                <Body2 className="tw-mt-7 tw-mb-2">{t("Files & Assets")}</Body2>
                <IntegrationCard
                  features={googleDriveIntegration.features}
                  context={googleDriveIntegration.context}
                  url={googleDriveIntegration.url}
                  title={googleDriveIntegration.title}
                  hasFeature={googleDriveIntegration.hasFeature}
                  img={googleDriveIntegration.img}
                  description={googleDriveIntegration.description}
                  integration={googleDriveIntegration.integration}
                  finished={googleDriveIntegration.finished}
                />

                <IntegrationCard
                  features={dropBoxIntegration.features}
                  context={dropBoxIntegration.context}
                  url={dropBoxIntegration.url}
                  title={dropBoxIntegration.title}
                  hasFeature={dropBoxIntegration.hasFeature}
                  img={dropBoxIntegration.img}
                  description={dropBoxIntegration.description}
                  integration={dropBoxIntegration.integration}
                  finished={dropBoxIntegration.finished}
                />
              </>
            ) : null}

            <Body2 className="tw-mt-7 tw-mb-2">{t("Utilities")}</Body2>
            <ApiTokensCard />
            {isMcpServerFeatureFlagOn ? (
              <IntegrationCard
                features={mcpServerIntegration.features}
                context={mcpServerIntegration.context}
                url={mcpServerIntegration.url}
                title={mcpServerIntegration.title}
                hasFeature={mcpServerIntegration.hasFeature}
                img={mcpServerIntegration.img}
                description={mcpServerIntegration.description}
                integration={mcpServerIntegration.integration}
                finished={mcpServerIntegration.finished}
              />
            ) : null}
            <IntegrationCard
              features={zapierIntegration.features}
              context={zapierIntegration.context}
              url={zapierIntegration.url}
              title={zapierIntegration.title}
              hasFeature={zapierIntegration.hasFeature}
              img={zapierIntegration.img}
              description={zapierIntegration.description}
              integration={zapierIntegration.integration}
              finished={zapierIntegration.finished}
            />
            <IntegrationCard
              features={webhooksIntegration.features}
              context={webhooksIntegration.context}
              url={webhooksIntegration.url}
              title={webhooksIntegration.title}
              hasFeature={webhooksIntegration.hasFeature}
              img={webhooksIntegration.img}
              description={webhooksIntegration.description}
              integration={webhooksIntegration.integration}
              finished={webhooksIntegration.finished}
            />
          </>
        ) : null}
      </Page>
    </ErrorBoundary>
  );
};

export default AppsAndIntegrationsPage;
